import { FormGroup } from "@blueprintjs/core";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import {
  AddressFieldValue,
  SmartAddressProps,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import { useSmartAddress } from "./useSmartAddress";
import TextField from "../../BaseInputFields/TextField/TextField";
import { EnumSingleSelectField } from "../../DropdownFields/EnumSingleSelectField/EnumSingleSelectField";
import { usStateOptions } from "../SimpleAddressField/defaults";
import SingleCheckboxField from "../../BaseInputFields/SingleCheckboxField/SingleCheckboxField";
import { AutoCompleteComponent } from "@syncfusion/ej2-react-dropdowns";
import { Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import { getErrorMessagesFromErrorMessages } from "../../../..";

export interface SmartAddressFieldProps extends SmartAddressProps {
  getAddressesByQuery: (
    address: Partial<AddressFieldValue>
  ) => Promise<AddressFieldValue[]>;
  getAddressByQuery: (address: AddressFieldValue) => Promise<AddressFieldValue>;
  shouldShowCounty?: boolean;
  // This should be set to true if you need the county on the selected address.
  // In practice, if you set shouldSetCountyOnSelect OR lookupSingleAddressOnSelect to `true`, both county and coordinates will be set.
  shouldSetCountyOnSelect?: boolean;
  isSmartOnly?: boolean;
  labelProps?: FieldLabeledProps;
  renderHasAutoSuggestToggle?: boolean;
  // This should be set to true if you need coordinates on the selected address.
  lookupSingleAddressOnSelect?: boolean;
}

export const SmartAddressField: React.FC<SmartAddressFieldProps> = (props) => {
  const {
    addressSuggestions,
    localAddress,
    shouldDisableAutoSuggest,
    clearCounter,
    handleFiltering,
    handleSelect,
    createFieldChangeHandler,
    handleAutoCompleteChange,
  } = useSmartAddress(
    props.getAddressesByQuery,
    props.getAddressByQuery,
    !!props.shouldSetCountyOnSelect,
    props.value,
    props.onSubmit,
    props.renderHasAutoSuggestToggle,
    props.lookupSingleAddressOnSelect ?? false
  );

  const itemTemplate = (data: AddressFieldValue) => {
    return (
      <div className="select__menu-item">
        <Icon icon={IconNames.MAP_MARKER} />
        <span>{data.fullAddress}</span>
      </div>
    );
  };

  const streetErrorMessages = getErrorMessagesFromErrorMessages(
    props.errorMessages,
    "Street"
  );

  return (
    <FormGroup>
      {props.label && (
        <div className="address-field-header">
          <FieldLabel
            labelName={props.label}
            tooltipDescription={
              props.description ||
              "Please input at least 3 characters for address look up"
            }
            fieldProps={{ ...props, value: localAddress }}
            isRequired={props.isRequired}
            {...props.labelProps}
          />
          {props.renderHasAutoSuggestToggle && !props.disabled && (
            <SingleCheckboxField
              displayAsSwitch
              text={"Auto-Suggest"}
              value={localAddress?.hasAutoSuggest}
              onSubmit={createFieldChangeHandler("hasAutoSuggest")}
            />
          )}
        </div>
      )}
      <div className="address-field_input address-street">
        <AutoCompleteComponent
          key={`street-${clearCounter}`}
          dataSource={addressSuggestions}
          fields={{ text: "street", value: "fullAddress" }}
          placeholder={"Type address to search..."}
          value={localAddress?.street}
          filtering={shouldDisableAutoSuggest ? undefined : handleFiltering}
          select={handleSelect}
          itemTemplate={itemTemplate}
          cssClass={`address_street_field ${
            props.isRequired && !localAddress?.street
              ? "has-required-background"
              : ""
          }`}
          disabled={props.disabled}
          readonly={props.disabled}
          change={handleAutoCompleteChange}
          popupHeight="300px"
          popupWidth="100%"
          noRecordsTemplate="Type address to search..."
          minLength={3}
          showPopupButton={false}
          autofill={false}
          allowFiltering={!shouldDisableAutoSuggest}
          enableVirtualization={false}
        />
        {streetErrorMessages && (
          <FormFieldErrorContainer errorMessages={streetErrorMessages} />
        )}
      </div>
      <div className="address-field_input additional-street">
        <TextField
          key={`street2-${clearCounter}`}
          type="Text"
          label=""
          placeholder="Street 2"
          value={localAddress?.street2}
          inputMode="text"
          disabled={props.disabled || props.isSmartOnly}
          onSubmit={createFieldChangeHandler("street2")}
          errorMessages={getErrorMessagesFromErrorMessages(
            props.errorMessages,
            "Street2"
          )}
        />
      </div>
      <div className="flex-field">
        <div className="address-field_input">
          <TextField
            key={`city-${clearCounter}`}
            type="Text"
            label=""
            placeholder="City"
            value={localAddress?.city}
            inputMode="text"
            disabled={props.disabled || props.isSmartOnly}
            width="20%"
            onSubmit={createFieldChangeHandler("city")}
            isRequired={props.isRequired}
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "City"
            )}
          />
        </div>
        <div className="address-field_input">
          <EnumSingleSelectField
            key={`state-${clearCounter}`}
            type="EnumSingleSelect"
            label=""
            placeholder="State"
            value={localAddress?.state}
            disabled={props.disabled || props.isSmartOnly}
            isRequired={props.isRequired}
            optionValues={usStateOptions}
            onSubmit={createFieldChangeHandler("state")}
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "State"
            )}
          />
        </div>
        <div className="address-field_input">
          <TextField
            key={`zip-${clearCounter}`}
            type="Text"
            label=""
            value={localAddress?.zip}
            placeholder="Zip"
            inputMode="text"
            disabled={props.disabled || props.isSmartOnly}
            onSubmit={createFieldChangeHandler("zip")}
            isRequired={props.isRequired}
            errorMessages={getErrorMessagesFromErrorMessages(
              props.errorMessages,
              "Zip"
            )}
          />
        </div>
        {(props.shouldShowCounty || props.shouldSetCountyOnSelect) && (
          <div className="address-field_input">
            <TextField
              key={`county-${clearCounter}`}
              type="Text"
              label=""
              value={localAddress?.county}
              placeholder={"County"}
              inputMode="text"
              disabled={props.disabled || props.isSmartOnly}
              onSubmit={createFieldChangeHandler("county")}
              errorMessages={getErrorMessagesFromErrorMessages(
                props.errorMessages,
                "County"
              )}
            />
          </div>
        )}
      </div>
      {props.errorMessages && (
        <>
          {props.errorMessages
            .filter((e) => typeof e === "string")
            .map((errorMessage, idx) => (
              <p key={idx} className="error-message">
                {errorMessage}
              </p>
            ))}
        </>
      )}
    </FormGroup>
  );
};
